import PrimaryInnerGlowButton from '@/components/buttons/primary';
import InputField from '@/components/forms/InputField';
import PhoneField from '@/components/forms/PhoneField';
import SelectField from '@/components/forms/SelectField';
import Image from '@/components/Image';
import { Container } from '@/components/layout/Container';
import Link from '@/components/Link';
import { PRIMARY_NAV_HEIGHT } from '@/components/navigation/PrimaryNavigation';
import YouTubeVideo from '@/components/YouTubeVideo';
import { GTM_VARIABLES } from '@/constants/gtm';
import {
  HubspotCompanySizeDropdownOptions,
  HubspotFieldLabels,
  HubspotFieldNames,
  HubspotFieldValidators,
  HubspotFormIDs,
  HubspotIndustryDropdownOptions,
} from '@/constants/hubspot';
import { WATCH_DEMO } from '@/constants/routes';
import { WATCH_DEMO_BYPASS_KEY } from '@/constants/session-storage';
import { GlobalContext } from '@/context';
import { ChiliPiperSession, ChiliPiperTargetID } from '@/lib/chili-piper';
import { useCustomFormik, useFormikHubspotSubmit } from '@/lib/formik';
import gtm from '@/lib/gtm';
import { setBypassCookie } from '@/lib/hooks/useBypassCookie';
import { checkHubspotEmailDomain } from '@/lib/hubspot';
import { parseHtml } from '@/lib/parse-html';
import { maybeNotSet } from '@/lib/typescript-helpers';
import { extractYouTubeId } from '@/lib/youtube';
import { COLORS } from '@/styles/color';
import { mediaBreakpointUp, paddingX, paddingY } from '@/styles/layout/utils';
import { FONT_WEIGHTS } from '@/styles/typography';
import { customTextSize, remCalc } from '@/styles/typography/utils';
import React, { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

const PAGE_GRADIENT_URL = '/assets/pages/request-demo/gradient-background.jpg';

// #region - Components

const PageWrapper = styled.section`
  padding-top: ${remCalc(PRIMARY_NAV_HEIGHT + 40)};
  background-color: ${COLORS.GRAY.HIGHLIGHT};

  ${mediaBreakpointUp('desktop')} {
    padding-top: ${remCalc(PRIMARY_NAV_HEIGHT + 120)};
  }
`;

const DemoContainer = styled(Container)`
  ${mediaBreakpointUp('desktop')} {
    ${paddingX(40)};
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: ${remCalc(20)};

  ${mediaBreakpointUp('tablet')} {
    grid-template-columns: repeat(12, 1fr);
    row-gap: ${remCalc(30)};
  }

  ${mediaBreakpointUp('desktop')} {
    grid-template-columns: repeat(18, 1fr);
  }
`;

const ContentColumn = styled.div`
  padding-top: ${remCalc(PRIMARY_NAV_HEIGHT)};
  grid-row: 1;
  grid-column: 1 / span 6;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    background: url(${PAGE_GRADIENT_URL}) no-repeat;
    background-size: 100% 100%;
    left: calc((100vw - 100%) / -2);
    height: calc(100% - 40px);
    width: 100vw;
    z-index: -1;
  }

  ${mediaBreakpointUp('tablet')} {
    grid-column: 1 / span 12;
  }

  ${mediaBreakpointUp('desktop')} {
    grid-column: 1 / span 7;
    &::before {
      height: calc(100% - ${remCalc(PRIMARY_NAV_HEIGHT)});
      width: calc(42.5% + ((100vw - 100%) / 2));
      left: auto;
      right: -${remCalc(60)};
    }
  }
`;

const Content = styled.div`
  ${mediaBreakpointUp('desktop')} {
    padding-top: ${remCalc(40)};
  }
`;

const Headline = styled.h1`
  ${customTextSize(32, 36)}
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  margin-bottom: ${remCalc(24)};

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(49, 58)}
    margin-bottom: ${remCalc(16)};
  }
`;

const QuoteWrapper = styled.div`
  padding-top: ${remCalc(40)};

  ${mediaBreakpointUp('tablet')} {
    ${paddingX(60)};
  }

  ${mediaBreakpointUp('desktop')} {
    padding-top: 0;
    ${paddingX(0)};
  }
`;

const Quotation = styled.div`
  ${customTextSize(24, 32)}
  padding-left: ${remCalc(20)};
  position: relative;

  &::before {
    content: '“';
    position: absolute;
    top: ${remCalc(5.6)};
    left: 0;
    font-size: ${remCalc(40)};
    ${mediaBreakpointUp('tablet')} {
      top: ${remCalc(6.8)};
    }
    ${mediaBreakpointUp('desktop')} {
      top: ${remCalc(6)};
    }
    ${mediaBreakpointUp('desktopWide')} {
      top: ${remCalc(2)};
    }
  }

  ${mediaBreakpointUp('tablet')} {
    ${customTextSize(20, 26)}
  }

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(24, 32)}
  }

  ${mediaBreakpointUp('desktopWide')} {
    ${customTextSize(32, 38)}
  }
`;

const QuoteeWrapper = styled.div`
  display: flex;
  column-gap: ${remCalc(10)};
  padding-top: ${remCalc(20)};

  ${mediaBreakpointUp('tablet')} {
    column-gap: ${remCalc(20)};
  }
`;

const QuoteeName = styled.div`
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
`;

const QuoteeTitle = styled.div`
  ${customTextSize(14, 20)};
`;

const QuoteeAvatar = styled.div`
  img {
    border-radius: 50%;
  }
`;

const Quotee = styled.div``;

const FormColumn = styled.div`
  ${paddingY(60, 40)}
  grid-column: 1 / span 6;
  grid-row: 2;

  ${mediaBreakpointUp('tablet')} {
    grid-column: 1 / span 12;
  }

  ${mediaBreakpointUp('desktop')} {
    padding-top: ${remCalc(PRIMARY_NAV_HEIGHT + 40)};
    grid-column: 9 / 19;
    grid-row: 1 / -1;
  }
`;

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;

  & > div {
    ${paddingX(24)};

    &:nth-child(1) {
      position: relative;
      flex-grow: 1;

      form {
        width: 100%;
        display: flex;
        flex-direction: column;
        row-gap: ${remCalc(20)};
        padding-top: 0;

        ${PrimaryInnerGlowButton} {
          align-self: flex-start;
          width: 100%;
        }
      }
    }

    &:nth-child(2) {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 1rem;
      text-align: center;
      position: sticky;
      bottom: 0;
      background-color: ${COLORS.WHITE};
      padding-bottom: ${remCalc(16)};
      z-index: 999;

      hr {
        border: none;
        width: 100%;
        height: 50px;
        box-shadow: 0 -20px 20px -20px #d5d5d5;
        margin: 0 auto -50px;
      }
    }
  }

  ${mediaBreakpointUp('tablet')} {
    & > div {
      &:first-child {
        form {
          ${PrimaryInnerGlowButton} {
            width: auto;
          }
        }
      }
    }
  }

  ${mediaBreakpointUp('desktop')} {
    & > div {
      ${paddingX(48)};

      &:first-child {
        min-height: 640px;

        form {
          padding-top: 40px;
        }
      }
    }
  }
`;

const FormTitle = styled.div`
  ${customTextSize(16, 20)};
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  margin-bottom: ${remCalc(20)};
  text-align: center;

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(24, 34)};
  }
`;

const FormSubTitle = styled.div`
  ${customTextSize(24, 34)}
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  margin-bottom: ${remCalc(30)};
  text-align: center;

  ${mediaBreakpointUp('tablet')} {
    margin-bottom: ${remCalc(50)};
  }

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(32, 38)};
    margin-bottom: ${remCalc(40)};
  }
`;

const FormFieldRow = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  row-gap: ${remCalc(20)};

  > div {
    flex-basis: 50%;
  }

  ${mediaBreakpointUp('desktop')} {
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
    column-gap: ${remCalc(20)};
  }
`;

const ContentWrapper = styled.div`
  background-color: ${COLORS.WHITE};
  border-radius: ${remCalc(10)};
  padding: ${remCalc(20)};
`;

const SuccessHeadline = styled(Headline)`
  text-align: center;

  ${mediaBreakpointUp('desktop')} {
    margin-bottom: ${remCalc(44)};
  }
`;

const SuccessStepWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  row-gap: ${remCalc(40)};
  padding: ${remCalc(20)};

  ${mediaBreakpointUp('desktop')} {
    flex-direction: row;
    justify-content: center;
    padding: ${remCalc(60, 0)};
  }
`;

const SuccessStep = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  ${mediaBreakpointUp('desktop')} {
    ${paddingX(34)}
    flex-basis: 1;
    max-width: ${remCalc(264)};
    box-sizing: content-box;
  }
`;

const SuccessStepHeadline = styled.p`
  ${customTextSize(16, 20)}
  font-weight: ${FONT_WEIGHTS.BOLD};
  text-align: center;
  margin-bottom: ${remCalc(16)};

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(24, 24)}
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  }
`;

const SuccessStepText = styled.p`
  ${customTextSize(16, 24)}
  text-align: center;

  a {
    text-decoration: underline;
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  }
`;

const SuccessStepIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  width: ${remCalc(164)};
  height: ${remCalc(164)};
  margin-bottom: ${remCalc(16)};

  background: radial-gradient(50% 50% at 50% 50%, rgba(255, 207, 0, 0.1) 0%, rgba(255, 207, 0, 0) 100%);
`;

const StyledYouTubeVideo = styled(YouTubeVideo)`
  border-radius: ${remCalc(10)};
  overflow: hidden;
  background-color: ${COLORS.WHITE};
`;

const LogoFarm = styled.div`
  display: none;
  ${paddingY(40)};

  h2 {
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
    ${customTextSize(16, 24)}
    margin-bottom: ${remCalc(20)};
  }

  ${mediaBreakpointUp('desktop')} {
    display: block;
  }

  ${mediaBreakpointUp('desktopWide')} {
    ${paddingY(60, 40)};
  }
`;

const LogoFarmList = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: start;
  column-gap: ${remCalc(32)};
  row-gap: ${remCalc(16)};

  ${mediaBreakpointUp('desktop')} {
    column-gap: ${remCalc(20)};
  }
`;

interface LogoFarmLogoProps {
  isTaller: boolean;
}

const LogoFarmLogo = styled.div<LogoFarmLogoProps>`
  img {
    width: auto;
    height: ${(props) => (props.isTaller ? 60 : 40)}px;
  }
`;

const LogoFarmContainer = styled.div<{ default: boolean }>`
  ${LogoFarm} {
    display: ${(props) => (props.default ? 'none' : 'block')};
    h2 {
      text-align: ${(props) => (props.default ? 'left' : 'center')};
    }

    ${mediaBreakpointUp('desktop')} {
      display: block;
    }
  }

  ${LogoFarmList} {
    justify-content: ${(props) => (props.default ? 'start' : 'center')};
  }
`;

const Awards = styled.div`
  display: none;
  ${mediaBreakpointUp('desktop')} {
    display: block;
    padding-bottom: ${remCalc(60)};
  }

  h2 {
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
    ${customTextSize(16, 24)}
    margin-bottom: ${remCalc(20)};
  }
`;

const AwardsList = styled.div`
  display: flex;
  column-gap: ${remCalc(20)};
  justify-content: space-between;
`;

const AwardLogo = styled.div``;

const PhoneConfirmationWrapper = styled.div`
  display: flex;
  flex-direction: column;
  text-align: center;

  p {
    margin-bottom: 1.5rem;
  }

  ${PrimaryInnerGlowButton} {
    margin: 0 auto;
  }
`;

// #endregion - Components

interface DemoPageLayoutProps {
  pageName: string;
  formHeadline?: string;
  formSubtitle?: string;
  awardsHeadline?: string;
  heroText: string;
  heroContent: React.ReactElement | string;
  socialProofHeadline: string;
  socialProofCustomers: {
    name: string;
    logo: {
      url: string;
      height: number;
      width: number;
      isTaller: boolean;
    };
  }[];
  videoUrl?: string;
  videoCover?: string;
  formSubmitted?: boolean;
  awards?: {
    badge: {
      title: string;
      url: string;
      height: number;
      width: number;
    };
  }[];
  quote?: {
    text: string;
    customer: {
      name: string;
      title: string;
      avatar: {
        url: string;
      };
    };
  }[];
}

const DemoPageLayout: FC<DemoPageLayoutProps> = (props) => {
  const { showGetStartedModal } = useContext(GlobalContext);
  const [formSubmitted, setFormSubmitted] = useState<boolean>(props.formSubmitted ?? false);
  const [showFullScreenConfirmation, setShowFullScreenConfirmation] = useState<boolean>(props.formSubmitted ?? false);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [showFindAnotherTimeCTA, setShowFindAnotherTimeCTA] = useState(false);
  const [meetingType, setMeetingType] = useState<'scheduled' | 'phone' | null>(null);
  const chiliPiperSessionRef = useRef<ChiliPiperSession | null>(null);

  useEffect(() => {
    if (props.formSubmitted) {
      setFormSubmitted(props.formSubmitted);
      setShowFullScreenConfirmation(props.formSubmitted);
    }
  }, [props.formSubmitted]);

  useEffect(() => {
    return () => {
      if (chiliPiperSessionRef.current) {
        chiliPiperSessionRef.current.destroy();
      }
    };
  }, []);

  const { youtubeVideoId, isVideoDemo, formId } = useMemo(() => {
    const youtubeVideoId = !!props.videoUrl ? extractYouTubeId(props.videoUrl) : null;
    const isVideoDemo = !!youtubeVideoId;
    const formId = isVideoDemo ? HubspotFormIDs.WatchDemo : HubspotFormIDs.RequestDemo;

    return {
      youtubeVideoId,
      isVideoDemo,
      formId,
    };
  }, [props.videoUrl]);

  const formik = useCustomFormik({
    initialValues: {
      [HubspotFieldNames.Email]: '',
      [HubspotFieldNames.FirstName]: '',
      [HubspotFieldNames.LastName]: '',
      [HubspotFieldNames.CompanyName]: '',
      [HubspotFieldNames.JobTitle]: '',
      [HubspotFieldNames.CompanySize]: '',
      [HubspotFieldNames.Industry]: '',
      [HubspotFieldNames.Phone]: '',
    },
    onSubmit: useFormikHubspotSubmit({
      formId,
      name: `${props.pageName} Form`,
      content: `${props.pageName} Form`,
      onSuccess: async (values) => {
        setFormSubmitted(true);

        chiliPiperSessionRef.current = new ChiliPiperSession(
          {
            email: values[HubspotFieldNames.Email],
            first_name: values[HubspotFieldNames.FirstName],
            last_name: values[HubspotFieldNames.LastName],
            company_name: values[HubspotFieldNames.CompanyName],
            company_size: values[HubspotFieldNames.CompanySize],
            industry: values[HubspotFieldNames.Industry],
            phone: values[HubspotFieldNames.Phone],
          },
          (eventName, eventArgs) => {
            switch (eventName) {
              case 'phone-selected':
                setShowFindAnotherTimeCTA(false);
                setMeetingType('phone');
                break;
              case 'availability-loaded':
                setMeetingType('scheduled');
                setShowFindAnotherTimeCTA(true);
                break;
              case 'timeslot-selected':
                setShowFindAnotherTimeCTA(false);
                break;
              case 'booked':
              case 'booking-confirmed':
                setShowConfirmation(true);
                break;
              case 'phoneClose':
                setShowConfirmation(true);
                break;
              case 'onClose':
              case 'onError':
                setShowFullScreenConfirmation(true);
                break;
            }
          },
        );

        if (formId === HubspotFormIDs.RequestDemo) {
          const { isPayingCustomer } = await checkHubspotEmailDomain(values[HubspotFieldNames.Email]);

          gtm.trackDemoRequest({
            [GTM_VARIABLES.USER_EMAIL]: values[HubspotFieldNames.Email],
            [GTM_VARIABLES.USER_FIRST_NAME]: values[HubspotFieldNames.FirstName],
            [GTM_VARIABLES.USER_LAST_NAME]: values[HubspotFieldNames.LastName],
            [GTM_VARIABLES.USER_PHONE]: values[HubspotFieldNames.Phone],
          });

          if (isPayingCustomer) {
            setShowFullScreenConfirmation(true);

            if (chiliPiperSessionRef.current) {
              chiliPiperSessionRef.current.markExistingCustomer();
            }
          } else {
            if (chiliPiperSessionRef.current) {
              chiliPiperSessionRef.current.loadFrame();
            }
          }
        } else {
          setShowFullScreenConfirmation(true);
        }
      },
    }),
    validate: (values) => {
      const errors = {};

      errors[HubspotFieldNames.Email] = HubspotFieldValidators[HubspotFieldNames.Email](
        values[HubspotFieldNames.Email],
        {
          required: true,
          allowPersonalEmail: true,
          allowSharedEmail: true,
        },
      );

      errors[HubspotFieldNames.FirstName] = HubspotFieldValidators[HubspotFieldNames.FirstName](
        values[HubspotFieldNames.FirstName],
        {
          required: true,
        },
      );

      errors[HubspotFieldNames.LastName] = HubspotFieldValidators[HubspotFieldNames.LastName](
        values[HubspotFieldNames.LastName],
        {
          required: true,
        },
      );

      errors[HubspotFieldNames.CompanyName] = HubspotFieldValidators[HubspotFieldNames.CompanyName](
        values[HubspotFieldNames.CompanyName],
        {
          required: true,
        },
      );

      errors[HubspotFieldNames.JobTitle] = HubspotFieldValidators[HubspotFieldNames.JobTitle](
        values[HubspotFieldNames.JobTitle],
        {
          required: true,
        },
      );

      errors[HubspotFieldNames.Phone] = HubspotFieldValidators[HubspotFieldNames.Phone](
        values[HubspotFieldNames.Phone],
        {
          required: true,
        },
      );

      errors[HubspotFieldNames.CompanySize] = HubspotFieldValidators[HubspotFieldNames.CompanySize](
        values[HubspotFieldNames.CompanySize],
        {
          required: true,
        },
      );

      errors[HubspotFieldNames.Industry] = HubspotFieldValidators[HubspotFieldNames.Industry](
        values[HubspotFieldNames.Industry],
        {
          required: true,
        },
      );

      if (Object.values(errors).every((val) => val === undefined)) return;

      return errors;
    },
  });

  const onDemoClick = () => {
    setBypassCookie(WATCH_DEMO_BYPASS_KEY);

    gtm.track('click', {
      element_type: 'link',
      element_label: 'Product Demo',
      location: `Form Success - ${props.pageName}`,
    });
  };

  const customerQuote = (
    <QuoteWrapper>
      {props.quote &&
        props.quote.map((quote) => {
          const customer = quote.customer;

          if (!customer) return null;

          return (
            <div key={customer.name}>
              <Quotation>{parseHtml(quote.text)}</Quotation>
              <QuoteeWrapper>
                <QuoteeAvatar>
                  <Image src={customer.avatar.url} width="50" height="50" alt={`${customer.name} logo`} />
                </QuoteeAvatar>
                <Quotee>
                  <QuoteeName>{customer.name}</QuoteeName>
                  <QuoteeTitle>{parseHtml(customer.title)}</QuoteeTitle>
                </Quotee>
              </QuoteeWrapper>
            </div>
          );
        })}
    </QuoteWrapper>
  );

  const logoFarm = (
    <LogoFarm>
      <h2>{props.socialProofHeadline}</h2>

      <LogoFarmList>
        {props.socialProofCustomers.map((customer) => {
          if (!customer.logo) return null;

          return (
            <LogoFarmLogo key={customer.name} isTaller={customer.logo.isTaller ?? false}>
              <Image
                src={customer.logo.url}
                width={customer.logo.width}
                height={customer.logo.height}
                alt={`${customer.name} logo`}
              />
            </LogoFarmLogo>
          );
        })}
      </LogoFarmList>
    </LogoFarm>
  );

  const awards = (
    <Awards>
      <h2>{props.awardsHeadline ?? "And we don't let them down"}</h2>

      <AwardsList>
        {props.awards &&
          props.awards.map((award, index) => {
            if (index > 3) return null; // Limit to top four results
            if (!award.badge) return null;

            return (
              <AwardLogo key={award.badge.title}>
                <Image
                  src={award.badge.url}
                  width={award.badge.width}
                  height={award.badge.height}
                  alt={`${award.badge.title} award`}
                />
              </AwardLogo>
            );
          })}
      </AwardsList>
    </Awards>
  );

  if (showFullScreenConfirmation) {
    if (isVideoDemo) {
      return (
        <PageWrapper>
          <Container>
            <SuccessHeadline>Here is your video demo</SuccessHeadline>

            <ContentWrapper>
              <StyledYouTubeVideo
                coverSrc={props.videoCover}
                videoId={youtubeVideoId!}
                onInitialPlay={() => {
                  gtm.track('play_video', {
                    location: `Form Success - ${props.pageName}`,
                    video_youtube_id: youtubeVideoId!,
                    video_url: maybeNotSet(props.videoUrl),
                  });
                }}
              />
            </ContentWrapper>

            <LogoFarmContainer default={false}>{logoFarm}</LogoFarmContainer>
          </Container>
        </PageWrapper>
      );
    }

    return (
      <PageWrapper>
        <Container>
          <SuccessHeadline>Thank you! What you can expect next.</SuccessHeadline>

          <ContentWrapper>
            <SuccessStepWrapper>
              <SuccessStep>
                <SuccessStepHeadline>1. We’ll be in touch</SuccessStepHeadline>
                <SuccessStepIcon>
                  <Image src="/webassets/pages/get-started/check-inbox.png" width={228} height={182} alt="" />
                </SuccessStepIcon>
                <SuccessStepText>
                  A product expert will call or email you soon to set up a time to chat.
                </SuccessStepText>
              </SuccessStep>

              <SuccessStep>
                <SuccessStepHeadline>2. Mark your calendar!</SuccessStepHeadline>
                <SuccessStepIcon>
                  <Image src="/webassets/pages/get-started/select-time.png" width={280} height={258} alt="" />
                </SuccessStepIcon>
                <SuccessStepText>
                  We’ll ask about your workflows to prepare a personalized demo that fits your team’s unique needs.
                </SuccessStepText>
              </SuccessStep>

              <SuccessStep>
                <SuccessStepHeadline>3. In the meantime</SuccessStepHeadline>
                <SuccessStepIcon>
                  <Image src="/webassets/pages/get-started/watch-video.png" width={264} height={216} alt="" />
                </SuccessStepIcon>
                <SuccessStepText>
                  Want to see Front while you’re waiting? Check out our{' '}
                  <Link href={WATCH_DEMO}>
                    <a onClick={onDemoClick}>video demo</a>
                  </Link>
                  .
                </SuccessStepText>
              </SuccessStep>
            </SuccessStepWrapper>
          </ContentWrapper>

          <LogoFarmContainer default={false}>{logoFarm}</LogoFarmContainer>
        </Container>
      </PageWrapper>
    );
  }

  return (
    <DemoContainer>
      <Grid>
        <ContentColumn>
          <Content>
            {customerQuote}
            <LogoFarmContainer default={true}>{logoFarm}</LogoFarmContainer>
            {awards}
          </Content>
        </ContentColumn>

        <FormColumn>
          <FormWrapper>
            <div>
              {formSubmitted ? null : (
                <>
                  <FormTitle>{props.formHeadline ?? 'Get started'}</FormTitle>
                  <FormSubTitle>{props.formSubtitle ?? 'Tell us a bit about you'}</FormSubTitle>

                  <form onSubmit={formik.handleSubmit}>
                    <InputField
                      type="email"
                      name={HubspotFieldNames.Email}
                      label={HubspotFieldLabels.Email}
                      placeholder="you@company.com"
                      value={formik.values[HubspotFieldNames.Email]}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={formik.touched[HubspotFieldNames.Email] && !!formik.errors[HubspotFieldNames.Email]}
                      errorMessage={formik.errors[HubspotFieldNames.Email] as string}
                    />

                    <FormFieldRow>
                      <InputField
                        type="text"
                        name={HubspotFieldNames.FirstName}
                        label={HubspotFieldLabels.FirstName}
                        placeholder="Jamie"
                        value={formik.values[HubspotFieldNames.FirstName]}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={
                          formik.touched[HubspotFieldNames.FirstName] && !!formik.errors[HubspotFieldNames.FirstName]
                        }
                        errorMessage={formik.errors[HubspotFieldNames.FirstName] as string}
                      />

                      <InputField
                        type="text"
                        name={HubspotFieldNames.LastName}
                        label={HubspotFieldLabels.LastName}
                        placeholder="Smith"
                        value={formik.values[HubspotFieldNames.LastName]}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={
                          formik.touched[HubspotFieldNames.LastName] && !!formik.errors[HubspotFieldNames.LastName]
                        }
                        errorMessage={formik.errors[HubspotFieldNames.LastName] as string}
                      />
                    </FormFieldRow>

                    <FormFieldRow>
                      <InputField
                        type="text"
                        name={HubspotFieldNames.CompanyName}
                        label={HubspotFieldLabels.CompanyName}
                        placeholder="Your Company"
                        value={formik.values[HubspotFieldNames.CompanyName]}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={
                          formik.touched[HubspotFieldNames.CompanyName] &&
                          !!formik.errors[HubspotFieldNames.CompanyName]
                        }
                        errorMessage={formik.errors[HubspotFieldNames.CompanyName] as string}
                      />

                      <InputField
                        type="text"
                        name={HubspotFieldNames.JobTitle}
                        label={HubspotFieldLabels.JobTitle}
                        placeholder="e.g., Director of Operations"
                        value={formik.values[HubspotFieldNames.JobTitle]}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={
                          formik.touched[HubspotFieldNames.JobTitle] && !!formik.errors[HubspotFieldNames.JobTitle]
                        }
                        errorMessage={formik.errors[HubspotFieldNames.JobTitle] as string}
                      />
                    </FormFieldRow>

                    <FormFieldRow>
                      <SelectField
                        name={HubspotFieldNames.CompanySize}
                        label={HubspotFieldLabels.CompanySize}
                        options={HubspotCompanySizeDropdownOptions}
                        value={formik.values[HubspotFieldNames.CompanySize]}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={
                          formik.touched[HubspotFieldNames.CompanySize] &&
                          !!formik.errors[HubspotFieldNames.CompanySize]
                        }
                        errorMessage={formik.errors[HubspotFieldNames.CompanySize] as string}
                      />

                      <SelectField
                        name={HubspotFieldNames.Industry}
                        label={HubspotFieldLabels.Industry}
                        options={HubspotIndustryDropdownOptions}
                        value={formik.values[HubspotFieldNames.Industry]}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={
                          formik.touched[HubspotFieldNames.Industry] && !!formik.errors[HubspotFieldNames.Industry]
                        }
                        errorMessage={formik.errors[HubspotFieldNames.Industry] as string}
                        translucent
                      />
                    </FormFieldRow>

                    <PhoneField
                      name={HubspotFieldNames.Phone}
                      label={HubspotFieldLabels.Phone}
                      value={formik.values[HubspotFieldNames.Phone]}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={formik.touched[HubspotFieldNames.Phone] && !!formik.errors[HubspotFieldNames.Phone]}
                      errorMessage={formik.errors[HubspotFieldNames.Phone] as string}
                    />

                    <PrimaryInnerGlowButton type="submit" disabled={formik.isValidating || formik.isSubmitting}>
                      {formik.isValidating || formik.isSubmitting ? 'Submitting' : 'Submit'}
                    </PrimaryInnerGlowButton>
                  </form>
                </>
              )}

              {showConfirmation && meetingType === 'phone' ? (
                <PhoneConfirmationWrapper>
                  <FormTitle>Thanks!</FormTitle>

                  <p>
                    Hopefully we were able to connect. If not, please schedule some dedicated time with our sales team.
                  </p>

                  <PrimaryInnerGlowButton
                    onClick={() => {
                      setShowFullScreenConfirmation(false);
                      setShowConfirmation(false);
                      setMeetingType(null);
                      setShowFindAnotherTimeCTA(false);

                      if (chiliPiperSessionRef.current) {
                        chiliPiperSessionRef.current.destroy();
                        chiliPiperSessionRef.current.loadFrame(true);
                      }
                    }}
                  >
                    Book a meeting
                  </PrimaryInnerGlowButton>
                </PhoneConfirmationWrapper>
              ) : null}

              {!isVideoDemo && !showGetStartedModal ? (
                <ChiliPiperTarget
                  id={ChiliPiperTargetID}
                  key="chilipiper"
                  style={{
                    display: showConfirmation && meetingType == 'phone' ? 'none' : 'block',
                  }}
                />
              ) : null}
            </div>

            {showConfirmation && meetingType === 'scheduled' ? (
              <div>
                <p>In the meantime, explore Front with our video demo.</p>

                <Link href={WATCH_DEMO}>
                  <PrimaryInnerGlowButton as="a" onClick={onDemoClick}>
                    View Demo
                  </PrimaryInnerGlowButton>
                </Link>
              </div>
            ) : null}

            {showFindAnotherTimeCTA ? (
              <div>
                <hr />

                <p>
                  <strong>Don&apos;t see a time that works for you?</strong>
                  <br />
                  Have a Front specialist reach out to find a time that fits your schedule
                </p>

                <PrimaryInnerGlowButton
                  onClick={() => {
                    setShowFullScreenConfirmation(true);

                    if (chiliPiperSessionRef.current) {
                      chiliPiperSessionRef.current.requestFollowUp();
                    }
                  }}
                >
                  Request a different time
                </PrimaryInnerGlowButton>
              </div>
            ) : null}
          </FormWrapper>
        </FormColumn>
      </Grid>
    </DemoContainer>
  );
};

DemoPageLayout.defaultProps = {
  formSubmitted: false,
};

export default DemoPageLayout;

const ChiliPiperTarget = styled.div`
  background-color: ${COLORS.WHITE};

  .chilipiper-popup {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    z-index: 999;
    background: ${COLORS.WHITE};
    border-radius: 10px;

    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;

    .chilipiper-close {
      display: none;
    }

    .chilipiper-popup-window {
      max-width: none !important; // inline style override
      max-height: none !important; // inline style override
      width: 100% !important; // inline style override
      height: 100% !important; // inline style override
      border-radius: 0 !important; // inline style override
      background: transparent;

      .routing-loader {
        position: absolute;
        width: inherit;
        max-width: inherit;
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        margin-top: -50px;

        .routing-loader-body {
          display: flex;
          flex-direction: column;
          align-items: center;

          span {
            margin-bottom: 40px;
            font-weight: 500;
            font-size: 18px;
            color: #808284;
          }
        }
      }
    }

    .chilipiper-frame {
      overflow: hidden;
      border-radius: 0 !important;
      width: 100%;
      height: 100%;
      min-width: 100%;
    }

    .fabe-first-loading-label {
      margin-top: 30px;
      margin-left: 30px;
      display: block;
    }

    .chilipiper-popup-loading {
      width: 50px;
      height: 50px;
      background-image: url('https://js.chilipiper.com/images/loading.gif');
      background-size: 100%;
    }

    [data-chili-css='loader-dot'] {
      width: 1rem;
      height: 1rem;
      background: rgba(38, 52, 64, 0.32);

      &.dot1,
      &.dot2,
      &.dot3 {
        border-radius: 100%;
        display: inline-block;
        animation: bounce 1.4s infinite ease-in-out both;
      }

      &.dot1 {
        animation-delay: -0.32s;
      }

      &.dot2 {
        animation-delay: -0.16s;
      }
    }
  }

  @keyframes bounce {
    0%,
    80%,
    100% {
      transform: scale(0);
    }
    40% {
      transform: scale(1);
    }
  }
`;
